import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Pill } from "@/components/perf";
import Avatar from "@/components/Avatar";
import PlayerName from "@/components/PlayerName";
import { ClipboardList, Check, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function LeagueRoster() {
  const [regs, setRegs] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/league/registrations");
    setRegs(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/league/registrations/${r.id}/approve`);
      toast.success(`${r.team_name} approved — BPL team created`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const reject = async (r) => {
    if (!window.confirm(`Remove "${r.team_name}" from the roster? (Refunds are handled manually in Stripe.)`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/league/registrations/${r.id}`);
      toast.success("Registration removed");
      await load();
    } finally { setBusyId(null); }
  };

  const pending = regs.filter((r) => r.status === "pending").length;

  return (
    <div className="mt-8" data-testid="league-roster-admin">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2">
        <ClipboardList className="w-6 h-6 text-pb-red" /> League Roster
        {pending > 0 && <Pill color="#F59E0B">{pending} pending</Pill>}
      </h3>
      <p className="text-sm text-zinc-500 mb-5">Self-registered teams for the upcoming league ($250 entry paid via Stripe). Approving creates an official BPL team in the player's division.</p>

      <div className="bg-pb-surface border border-pb-border">
        {regs.length === 0 ? (
          <div className="p-5 text-sm text-zinc-600" data-testid="league-roster-empty">No league registrations yet.</div>
        ) : (
          <div className="divide-y divide-pb-border/50">
            {regs.map((r) => (
              <div key={r.id} data-testid={`league-reg-${r.id}`} className="px-5 py-4 flex flex-wrap items-center gap-3">
                <Avatar src={r.user_avatar} name={r.user_name} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold uppercase truncate">{r.team_name}</div>
                  <div className="text-xs text-zinc-500">
                    <PlayerName userId={r.user_id} name={r.user_name} />{r.partner_name ? <> + <PlayerName userId={r.partner_id} name={r.partner_name} /></> : " (partner TBD)"} · ${Number(r.amount || 0).toFixed(2)} paid
                    {r.partner_status === "pending_payment" && <span className="text-amber-400 font-bold uppercase text-[10px] tracking-widest ml-1" data-testid={`partner-pending-${r.id}`}>· partner payment pending</span>}
                    {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
                  </div>
                  {(r.city || r.facility) && (
                    <div className="text-[11px] text-pb-cyan/80 mt-0.5">
                      {[r.city && `${r.city}${r.state ? `, ${r.state}` : ""}`, r.facility].filter(Boolean).join(" · ")}
                      {r.levels?.length > 0 && <span className="text-zinc-500"> · Levels: {r.levels.join(", ")}</span>}
                    </div>
                  )}
                  {r.psi_division && r.levels?.length > 0 && !r.levels.includes(r.psi_division) && (
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-400 mt-1" data-testid={`level-mismatch-${r.id}`}>
                      <AlertTriangle className="w-3 h-3" /> Level mismatch — PSI says {r.psi_division}
                    </div>
                  )}
                </div>
                <Pill color="#4ADE80">{r.division}</Pill>
                <Pill color={r.status === "approved" ? "#4ADE80" : "#F59E0B"}>{r.status}</Pill>
                {r.status === "pending" && (
                  <button data-testid={`approve-reg-${r.id}`} onClick={() => approve(r)} disabled={busyId === r.id}
                    className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest px-3 py-1.5 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                )}
                <button data-testid={`reject-reg-${r.id}`} onClick={() => reject(r)} disabled={busyId === r.id}
                  className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
